/* ОСНОВА.Эксперт — чат руководителя (функция Руководителя, страница /expert).
   Зеркало `osnova_progress::advisor` и `osnova_storage::progress::advisor`. */

export interface AdvisorSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

/** Вложение реплики. У вопроса — приложенный файл (хранится только описание,
 *  не текст); у ответа — файл, созданный Экспертом (`kind: 'file'`), его можно
 *  скачать по `file_id`. */
export interface AdvisorAttachmentMeta {
  name: string;
  kind: 'document' | 'image' | 'file';
  /** Сколько символов текста извлечено (0 — не распознано); у файлов ответа нет. */
  chars?: number;
  /** Файл ответа: идентификатор для скачивания, тип и размер в байтах. */
  file_id?: string;
  mime?: string;
  size?: number;
}

export interface AdvisorMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments: AdvisorAttachmentMeta[];
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd_micro: number;
  model: string | null;
  created_at: string;
}

export interface AdvisorUsage {
  /** `YYYY-MM` по Москве. */
  month: string;
  spent_usd: number;
  limit_usd: number;
  remaining_usd: number;
  exhausted: boolean;
  model: string;
  /** Модель ищет в интернете (OpenAI Responses API). */
  web_search: boolean;
  /** Модель создаёт файлы — PDF, Word, Excel, PowerPoint (Code Interpreter). */
  files: boolean;
  /** Модель смотрит данные ОСНОВЫ: договоры, переписку, объекты, совещания. */
  osnova_data: boolean;
}

export interface AdvisorAnswer {
  session: AdvisorSession;
  question: AdvisorMessage;
  answer: AdvisorMessage;
  usage: AdvisorUsage;
}

/** Расход руководителя за месяц — для администратора. */
export interface AdvisorSpend {
  user_id: string;
  full_name: string;
  email: string;
  spent_usd: number;
  limit_usd: number;
  personal_limit: boolean;
}

export interface AdvisorAskInput {
  session_id: string | null;
  question: string;
  files: File[];
}

/** Ограничения как на бэкенде (`progress_advisor.rs`, `advisor.rs`). */
export const ADVISOR_MAX_FILES = 5;
export const ADVISOR_MAX_FILE_MB = 25;
export const ADVISOR_MAX_QUESTION = 8000;
export const ADVISOR_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.xlsb,.txt,.rtf,.odt,.ods,.csv,.zip,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.tif,.tiff,.bmp';

/** Доллары: «$0,42», «$12,50». */
export function fmtUsd(v: number): string {
  return `$${v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Цена одного ответа: копейки показываем точнее — «$0,0031». */
export function fmtUsdMicro(micro: number): string {
  const v = micro / 1_000_000;
  if (v > 0 && v < 0.01) {
    return `$${v.toLocaleString('ru-RU', { maximumFractionDigits: 4 })}`;
  }
  return fmtUsd(v);
}

/** Месяц `2026-10` → «октябрь 2026». */
export function fmtAdvisorMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

/** Доля потраченного лимита, 0…1. */
export function advisorSpentShare(u: AdvisorUsage): number {
  if (u.limit_usd <= 0) return 1;
  return Math.min(1, Math.max(0, u.spent_usd / u.limit_usd));
}
